import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { MinnowLogo } from '../components/MinnowLogo';

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { handleGoogleCallback, user } = useAuth();
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(true);
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Use ref to prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processCallback = async () => {
      try {
        // Extract session_id from URL hash (format: #session_id=xxx)
        const hash = location.hash;
        const params = new URLSearchParams(hash.replace('#', ''));
        const sessionId = params.get('session_id');

        if (!sessionId) {
          // Check query params as fallback
          const queryParams = new URLSearchParams(location.search);
          const querySessionId = queryParams.get('session_id');
          
          if (!querySessionId) {
            setError('No session ID found');
            setProcessing(false);
            return;
          }
          
          await handleGoogleCallback(querySessionId);
        } else {
          await handleGoogleCallback(sessionId);
        }
        
        // Clear the hash from URL after processing
        window.history.replaceState(null, '', window.location.pathname);

        toast.success('Welcome to Minnow School!');
      } catch (err) {
        console.error('Auth callback error:', err);
        setError(err.message || 'Authentication failed');
        toast.error('Google sign-in failed. Please try again.');
      } finally {
        setProcessing(false);
      }
    };

    processCallback();
  }, [location, handleGoogleCallback]);

  // Redirect when user is set
  useEffect(() => {
    if (user && !processing) {
      if (!user.onboarding_complete) {
        navigate('/onboarding');
      } else {
        navigate('/dashboard');
      }
    }
  }, [user, processing, navigate]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#1a3a52] flex items-center justify-center">
        <div className="text-center text-white">
          <MinnowLogo variant="white" size={60} className="mx-auto mb-6" />
          <h2 className="text-xl font-semibold mb-2">Authentication Error</h2>
          <p className="text-white/70 mb-4">{error}</p>
          <button
            onClick={() => navigate('/login')}
            className="px-6 py-2 bg-white text-[#1a3a52] rounded-xl hover:bg-white/90 transition-colors"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1a3a52] flex items-center justify-center">
      <div className="text-center text-white">
        <MinnowLogo variant="white" size={60} className="mx-auto mb-6" />
        <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
        <h2 className="text-xl font-semibold">Signing you in...</h2>
        <p className="text-white/70 mt-2">Please wait while we complete your authentication</p>
      </div>
    </div>
  );
};

export default AuthCallback;
